import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import {
    Building2,
    HardHat,
    LayoutDashboard,
    QrCode,
    Settings,
    UserCog,
    Users,
} from "lucide-react";
import { searchService } from "@/services/search.service";
import { useDebounce } from "@/hooks/useDebounce";

const PAGES = [
    { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard, testid: "cmd-page-dashboard" },
    { label: "Clientes", to: "/customers", icon: Users, testid: "cmd-page-customers" },
    { label: "Ativos de Engenharia", to: "/assets", icon: QrCode, testid: "cmd-page-assets" },
    { label: "Técnicos", to: "/technicians", icon: HardHat, testid: "cmd-page-technicians" },
    { label: "Empresas", to: "/companies", icon: Building2, testid: "cmd-page-companies" },
    { label: "Usuários", to: "/users", icon: UserCog, testid: "cmd-page-users" },
    { label: "Configurações", to: "/settings", icon: Settings, testid: "cmd-page-settings" },
];

export function GlobalSearch({ open, onOpenChange }) {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const debounced = useDebounce(q, 250);
    const [results, setResults] = useState({ customers: [], assets: [], users: [], technicians: [] });

    // Keyboard shortcut
    useEffect(() => {
        function handler(e) {
            const isMod = e.metaKey || e.ctrlKey;
            if (isMod && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                onOpenChange(true);
            }
        }
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onOpenChange]);

    // Fetch results with debounce
    useEffect(() => {
        let cancelled = false;
        if (!debounced || debounced.length < 2) {
            setResults({ customers: [], assets: [], users: [], technicians: [] });
            return;
        }
        searchService
            .global(debounced, 5)
            .then((r) => !cancelled && setResults(r))
            .catch(() => !cancelled && setResults({ customers: [], assets: [], users: [], technicians: [] }));
        return () => {
            cancelled = true;
        };
    }, [debounced]);

    function go(to) {
        onOpenChange(false);
        setQ("");
        navigate(to);
    }

    return (
        <CommandDialog open={open} onOpenChange={onOpenChange}>
            <CommandInput
                placeholder="Buscar em Prosper360…"
                value={q}
                onValueChange={setQ}
                data-testid="global-search-input"
            />
            <CommandList data-testid="global-search-list">
                <CommandEmpty>Nenhum resultado para sua busca.</CommandEmpty>

                <CommandGroup heading="Páginas">
                    {PAGES.map((p) => (
                        <CommandItem key={p.to} onSelect={() => go(p.to)} data-testid={p.testid}>
                            <p.icon className="mr-2 h-4 w-4" />
                            <span>{p.label}</span>
                        </CommandItem>
                    ))}
                </CommandGroup>

                {results.customers?.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Clientes">
                            {results.customers.map((c) => (
                                <CommandItem
                                    key={`c-${c.id}`}
                                    onSelect={() => go(`/customers/${c.id}`)}
                                    data-testid={`cmd-result-customer-${c.id}`}
                                >
                                    <Users className="mr-2 h-4 w-4" />
                                    <div className="flex flex-col">
                                        <span>{c.legal_name}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {[c.trade_name, c.city].filter(Boolean).join(" · ")}
                                        </span>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}

                {results.assets?.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Ativos">
                            {results.assets.map((a) => (
                                <CommandItem
                                    key={`a-${a.id}`}
                                    onSelect={() => go(`/assets/${a.id}`)}
                                    data-testid={`cmd-result-asset-${a.id}`}
                                >
                                    <QrCode className="mr-2 h-4 w-4" />
                                    <div className="flex flex-col">
                                        <span>
                                            {a.internal_code} · {a.category}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {[a.manufacturer, a.model].filter(Boolean).join(" · ")}
                                        </span>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}

                {results.technicians?.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Técnicos">
                            {results.technicians.map((t) => (
                                <CommandItem
                                    key={`t-${t.id}`}
                                    onSelect={() => go(`/technicians/${t.id}`)}
                                    data-testid={`cmd-result-technician-${t.id}`}
                                >
                                    <HardHat className="mr-2 h-4 w-4" />
                                    <span>{t.name}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}

                {results.users?.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Usuários">
                            {results.users.map((u) => (
                                <CommandItem
                                    key={`u-${u.id}`}
                                    onSelect={() => go(`/users/${u.id}`)}
                                    data-testid={`cmd-result-user-${u.id}`}
                                >
                                    <UserCog className="mr-2 h-4 w-4" />
                                    <div className="flex flex-col">
                                        <span>{u.name}</span>
                                        <span className="text-xs text-muted-foreground">{u.email}</span>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}
            </CommandList>
        </CommandDialog>
    );
}
